/**
 * Whitelabel Configuration Utility
 *
 * Provides utilities for detecting whitelabel mode and default configuration values.
 * This module works both server-side and client-side via the NEXT_PUBLIC_ prefix.
 *
 * @module whitelabel-config
 */

/**
 * Default color and branding values for whitelabel tenants.
 * These values are used when a tenant hasn't configured custom values.
 */
export const WHITELABEL_DEFAULTS = {
  /** Default accent color (emerald green) */
  accentColor: '#10B981',
  /** Default page background color (white) */
  backgroundColor: '#FFFFFF',
  /** Default text color (dark gray) */
  textColor: '#111827',
  /** Default footer background color (dark gray) */
  footerBackgroundColor: '#111827',
  /** Default footer text color (gray) */
  footerTextColor: '#9CA3AF',
  /** Default footer link color (blue) */
  footerLinkColor: '#3B82F6',
  /** Default header background color (white) */
  headerBackgroundColor: '#FFFFFF',
  /** Default header text color (dark gray) */
  headerTextColor: '#111827',
  /** Default header link color (blue) */
  headerLinkColor: '#3B82F6',
  /** Default sticky header setting */
  headerSticky: false,
  /** Default transparent initial header setting */
  headerTransparentInitial: false,
  /** Default header background when scrolled */
  headerScrolledBackgroundColor: '#FFFFFF',
  /** Default header text color when scrolled */
  headerScrolledTextColor: '#111827',
  /** Default header button background color */
  headerButtonBackgroundColor: '#3B82F6',
  /** Default header button text color */
  headerButtonTextColor: '#FFFFFF',
  /** Default header button background when scrolled */
  headerScrolledButtonBackgroundColor: '#3B82F6',
  /** Default header button text color when scrolled */
  headerScrolledButtonTextColor: '#FFFFFF',
} as const;

/**
 * Supported social media platforms for tenant configuration.
 * These are the platforms that can be configured in the social links settings.
 */
export const SUPPORTED_SOCIAL_PLATFORMS = [
  'twitter',
  'linkedin',
  'instagram',
  'facebook',
  'youtube',
  'tiktok',
  'discord',
] as const;

/**
 * Type for supported social platforms
 */
export type SupportedSocialPlatform = (typeof SUPPORTED_SOCIAL_PLATFORMS)[number];

/**
 * Email template types supported by the system.
 * Each type corresponds to a specific transactional email.
 */
export const EMAIL_TEMPLATE_TYPES = [
  'WELCOME',
  'CAMPAIGN_INVITATION',
  'CLAIM_CONFIRMATION',
  'PASSWORD_RESET',
] as const;

/**
 * Type for email template types
 */
export type EmailTemplateType = (typeof EMAIL_TEMPLATE_TYPES)[number];

/**
 * Tenant page section types for configurable page layouts.
 * The 'campaign-list' section is required and cannot be removed.
 */
export const TENANT_PAGE_SECTION_TYPES = [
  'hero',
  'featured-campaigns',
  'campaign-list',
  'how-it-works',
  'custom-text',
] as const;

/**
 * Type for tenant page section types
 */
export type TenantPageSectionType = (typeof TENANT_PAGE_SECTION_TYPES)[number];

/**
 * Social link configuration structure for JSON storage.
 */
export interface SocialLink {
  platform: SupportedSocialPlatform;
  url: string;
  visible: boolean;
}

/**
 * Checks whether the application is running in whitelabel mode.
 *
 * Whitelabel mode hides SaaS-specific UI elements and enables
 * tenant-specific branding throughout the application.
 *
 * @returns {boolean} True if whitelabel mode is enabled, false otherwise.
 * @default false - Maintains backward compatibility with SaaS mode
 *
 * @example
 * ```typescript
 * import { isWhitelabelMode } from '@/lib/whitelabel-config';
 *
 * if (isWhitelabelMode()) {
 *   // Hide subscription prompts
 *   // Show tenant branding
 * }
 * ```
 */
export function isWhitelabelMode(): boolean {
  const mode = process.env.NEXT_PUBLIC_WHITELABEL_MODE;

  // Default to false for backward compatibility
  if (!mode) {
    return false;
  }

  // Only accept explicit 'true' (case-insensitive)
  return mode.toLowerCase() === 'true';
}

/**
 * Validates if a given platform is a supported social platform.
 *
 * @param platform - The platform string to validate
 * @returns True if the platform is supported
 */
export function isSupportedSocialPlatform(
  platform: string
): platform is SupportedSocialPlatform {
  return SUPPORTED_SOCIAL_PLATFORMS.includes(platform as SupportedSocialPlatform);
}

/**
 * Validates if a given template type is a supported email template type.
 *
 * @param templateType - The template type string to validate
 * @returns True if the template type is supported
 */
export function isEmailTemplateType(
  templateType: string
): templateType is EmailTemplateType {
  return EMAIL_TEMPLATE_TYPES.includes(templateType as EmailTemplateType);
}

/**
 * Validates if a given section type is a supported tenant page section type.
 *
 * @param sectionType - The section type string to validate
 * @returns True if the section type is supported
 */
export function isTenantPageSectionType(
  sectionType: string
): sectionType is TenantPageSectionType {
  return TENANT_PAGE_SECTION_TYPES.includes(sectionType as TenantPageSectionType);
}

/**
 * Checks if a section type is removable.
 * The 'campaign-list' section is a core section and cannot be removed.
 *
 * @param sectionType - The section type to check
 * @returns True if the section can be removed/hidden
 */
export function isSectionRemovable(sectionType: TenantPageSectionType): boolean {
  return sectionType !== 'campaign-list';
}
